
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Image as ImageIcon, Share2, Eye, Download, Mail, Copy, ExternalLink, Search, Filter, ChevronLeft, ChevronRight, Bell, Link2, AlertCircle, Trash2, CheckSquare, Square } from 'lucide-react';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AdminImageRecord {
  id: string;
  prompt: string;
  image_url: string;
  model: string;
  size: string;
  quality: string;
  style: string;
  created_at: string;
  profile_email: string;
  profile_name: string;
  share_count: number;
  is_shared: boolean;
  share_token: string;
  shared_at: string;
  shared_by_admin_email: string;
  admin_notes: string;
}

const AdminImageSharing = () => {
  const { user } = useSecureAuthContext();
  const [images, setImages] = useState<AdminImageRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterShared, setFilterShared] = useState('all');
  const [selectedImage, setSelectedImage] = useState<AdminImageRecord | null>(null);
  const [shareEmail, setShareEmail] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [sharing, setSharing] = useState(false);
  
  // Selection and deletion state
  const [selectedImageIds, setSelectedImageIds] = useState<string[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 12;

  useEffect(() => {
    fetchImages();
  }, [currentPage, filterShared]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      console.log('Fetching images from admin panel...');
      
      // Calculate offset for pagination
      const offset = (currentPage - 1) * itemsPerPage;
      
      // Build the query - fetch one extra item to check if there are more pages
      let imageQuery = supabase
        .from('generated_images')
        .select('*')
        .order('created_at', { ascending: false })
        .range(offset, offset + itemsPerPage);

      // Apply shared filter if specified
      if (filterShared === 'shared') {
        imageQuery = imageQuery.eq('is_shared', true);
      } else if (filterShared === 'unshared') {
        imageQuery = imageQuery.eq('is_shared', false);
      }

      const { data: imagesData, error: imagesError } = await imageQuery;

      if (imagesError) {
        console.error('Error fetching images:', imagesError);
        throw imagesError;
      }

      console.log('Fetched images data:', imagesData);

      if (!imagesData || imagesData.length === 0) {
        console.log('No images found');
        setImages([]);
        setTotalCount(offset);
        return;
      }

      // Check if we have more pages (we fetched itemsPerPage + 1)
      const hasMore = imagesData.length > itemsPerPage;
      const displayImages = hasMore ? imagesData.slice(0, itemsPerPage) : imagesData;
      const estimatedCount = hasMore ? offset + itemsPerPage + 1 : offset + imagesData.length;

      // Get unique user IDs from the display images
      const userIds = [...new Set(displayImages.map(img => img.user_id).filter(Boolean))];
      console.log('User IDs to fetch profiles for:', userIds);
      
      // Fetch profiles for these user IDs if there are any
      let profilesData = [];
      if (userIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, email, full_name')
          .in('id', userIds);

        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
        } else {
          profilesData = profiles || [];
        }
      }

      console.log('Fetched profiles:', profilesData);

      // Create a map of user_id to profile info
      const profilesMap = new Map(
        profilesData.map(profile => [profile.id, profile])
      );

      // Transform the display data to match our interface
      const transformedData = displayImages.map(item => {
        const profile = profilesMap.get(item.user_id);
        return {
          id: item.id,
          prompt: item.prompt || '',
          image_url: item.image_url || '',
          model: item.model || '',
          size: item.size || '',
          quality: item.quality || '',
          style: item.style || '',
          created_at: item.created_at || '',
          profile_email: profile?.email || 'Unknown',
          profile_name: profile?.full_name || 'Unknown User',
          share_count: 0, // We'll calculate this separately if needed
          is_shared: item.is_shared || false,
          share_token: item.share_token || '',
          shared_at: item.shared_at || '',
          shared_by_admin_email: item.shared_by_admin_email || '',
          admin_notes: item.admin_notes || ''
        };
      });

      console.log('Transformed data:', transformedData);
      console.log('Sample image URLs:', transformedData.slice(0, 3).map(img => ({
        id: img.id,
        url_length: img.image_url?.length || 0,
        url_type: img.image_url?.startsWith('data:') ? 'base64' : 'url',
        url_preview: img.image_url?.substring(0, 100)
      })));
      setImages(transformedData);
      setTotalCount(estimatedCount);
    } catch (error) {
      console.error('Error fetching images:', error);
      toast.error('Failed to load images: ' + (error.message || 'Unknown error'));
      setImages([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Apply client-side search filter to the loaded images
  const filteredImages = images.filter(image => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return image.prompt.toLowerCase().includes(searchLower) ||
           image.profile_email.toLowerCase().includes(searchLower) ||
           image.model.toLowerCase().includes(searchLower);
  });

  const generateShareToken = async (imageId: string) => {
    try {
      const { data, error } = await supabase.rpc('generate_share_token');
      if (error) throw error;
      
      const shareToken = data;
      
      const { error: updateError } = await supabase
        .from('generated_images')
        .update({
          share_token: shareToken,
          is_shared: true,
          shared_at: new Date().toISOString(),
          shared_by_admin_email: user?.email
        })
        .eq('id', imageId);

      if (updateError) throw updateError;
      
      return shareToken;
    } catch (error) {
      console.error('Error generating share token:', error);
      throw error;
    }
  };

  const shareImage = async () => {
    if (!selectedImage || !shareEmail.trim()) {
      toast.error('Please enter a recipient email');
      return;
    }

    setSharing(true);
    try {
      let shareToken = selectedImage.share_token;
      
      if (!shareToken) {
        shareToken = await generateShareToken(selectedImage.id);
      }

      // Insert into image_shares table (existing functionality)
      const { error: shareError } = await supabase
        .from('image_shares')
        .insert({
          image_id: selectedImage.id,
          admin_email: user?.email,
          recipient_email: shareEmail.trim(),
          share_token: shareToken,
          message: shareMessage.trim() || null
        });

      if (shareError) throw shareError;

      // NEW: Create in-app notification for the user
      const { data: recipientProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', shareEmail.trim())
        .single();

      if (recipientProfile) {
        const { error: notificationError } = await supabase
          .from('notifications')
          .insert({
            user_id: recipientProfile.id,
            admin_email: user?.email,
            image_id: selectedImage.id,
            share_token: shareToken,
            message: shareMessage.trim() || `An image has been shared with you by ${user?.email}`
          });

        if (notificationError) {
          console.error('Error creating notification:', notificationError);
        }
      }

      toast.success(`Image shared successfully with ${shareEmail}. ${recipientProfile ? 'In-app notification sent.' : 'Manual share link generated.'}`);
      setShareEmail('');
      setShareMessage('');
      setSelectedImage(null);
      fetchImages();
    } catch (error) {
      console.error('Error sharing image:', error);
      toast.error('Failed to share image');
    } finally {
      setSharing(false);
    }
  };

  const generateShareLink = async (imageId: string) => {
    try {
      let shareToken = images.find(img => img.id === imageId)?.share_token;
      
      if (!shareToken) {
        shareToken = await generateShareToken(imageId);
        fetchImages(); // Refresh to show updated token
      }
      
      const shareUrl = `${window.location.origin}/shared/${shareToken}`;
      navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied to clipboard!');
      
      return shareUrl;
    } catch (error) {
      console.error('Error generating share link:', error);
      toast.error('Failed to generate share link');
    }
  };

  const copyShareLink = (shareToken: string) => {
    const shareUrl = `${window.location.origin}/shared/${shareToken}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success('Share link copied to clipboard!');
  };

  const updateAdminNotes = async (imageId: string, notes: string) => {
    try {
      const { error } = await supabase
        .from('generated_images')
        .update({ admin_notes: notes })
        .eq('id', imageId);

      if (error) throw error;
      
      toast.success('Notes updated successfully');
      fetchImages();
    } catch (error) {
      console.error('Error updating notes:', error);
      toast.error('Failed to update notes');
    }
  };

  // Selection functions
  const toggleImageSelection = (imageId: string) => {
    setSelectedImageIds(prev => 
      prev.includes(imageId) 
        ? prev.filter(id => id !== imageId)
        : [...prev, imageId]
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedImageIds([]);
    } else {
      setSelectedImageIds(filteredImages.map(img => img.id));
    }
    setSelectAll(!selectAll);
  };

  // Clear selection when filters change
  useEffect(() => {
    setSelectedImageIds([]);
    setSelectAll(false);
  }, [filterShared, searchTerm]);

  // Delete function
  const deleteSelectedImages = async () => {
    if (selectedImageIds.length === 0) {
      toast.error('Please select images to delete');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedImageIds.length} image(s)? This action cannot be undone.`;
    if (!confirm(confirmMessage)) {
      return;
    }

    setDeleting(true);
    try {
      // Delete from database
      const { error } = await supabase
        .from('generated_images')
        .delete()
        .in('id', selectedImageIds);

      if (error) throw error;

      toast.success(`Successfully deleted ${selectedImageIds.length} image(s)`);
      
      // Clear selection and refresh
      setSelectedImageIds([]);
      setSelectAll(false);
      fetchImages();
    } catch (error) {
      console.error('Error deleting images:', error);
      toast.error('Failed to delete images: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  if (loading) {
    return (
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardContent className="p-6">
          <div className="text-center text-gray-400 py-12">
            <ImageIcon className="w-12 h-12 mx-auto mb-4" />
            <p className="text-white">Loading images...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center">
            <Share2 className="w-6 h-6 mr-2" />
            Image Sharing Management
          </CardTitle>
          
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mt-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by prompt, user email, or model..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
            </div>
            
            <Select value={filterShared} onValueChange={setFilterShared}>
              <SelectTrigger className="w-48 bg-white/10 border-white/20 text-white">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Images</SelectItem>
                <SelectItem value="shared">Shared Only</SelectItem>
                <SelectItem value="unshared">Unshared Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Selection and Delete Controls */}
          {filteredImages.length > 0 && (
            <div className="flex flex-col sm:flex-row gap-4 mt-4 p-4 bg-white/5 rounded-lg border border-white/10">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleSelectAll}
                  className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                >
                  {selectAll ? <CheckSquare className="w-4 h-4 mr-2" /> : <Square className="w-4 h-4 mr-2" />}
                  {selectAll ? 'Deselect All' : 'Select All'}
                </Button>
                <span className="text-sm text-gray-400">
                  {selectedImageIds.length > 0 && `${selectedImageIds.length} selected`}
                </span>
              </div>
              
              {selectedImageIds.length > 0 && (
                <Button
                  onClick={deleteSelectedImages}
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  size="sm"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {deleting ? 'Deleting...' : `Delete Selected (${selectedImageIds.length})`}
                </Button>
              )}
            </div>
          )}

          {/* Pagination Info */}
          <div className="text-sm text-gray-400">
            Showing {filteredImages.length} of {totalCount} images (Page {currentPage} of {totalPages})
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
            {filteredImages.map((image) => (
              <div key={image.id} className="relative group">
                <div className={`bg-white/5 rounded-xl overflow-hidden hover:bg-white/10 transition-all duration-300 border ${
                  selectedImageIds.includes(image.id) 
                    ? 'border-blue-500 ring-2 ring-blue-500/50' 
                    : 'border-white/10 hover:border-white/20'
                }`}>
                  {/* Selection checkbox */}
                  <div className="absolute top-2 left-2 z-20">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleImageSelection(image.id);
                      }}
                      className="w-6 h-6 p-0 bg-black/50 hover:bg-black/70 border border-white/20"
                    >
                      {selectedImageIds.includes(image.id) ? (
                        <CheckSquare className="w-4 h-4 text-blue-400" />
                      ) : (
                        <Square className="w-4 h-4 text-white" />
                      )}
                    </Button>
                  </div>
                  <div className="relative aspect-square bg-gray-800">
                    {/* Use the same image rendering approach as CompleteGallery */}
                    <img
                      src={image.image_url}
                      alt={image.prompt}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzM3NDE0OSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5Q0E0QUYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBOb3QgRm91bmQ8L3RleHQ+PC9zdmc+';
                      }}
                    />
                    
                    <div className="absolute top-2 right-2 flex gap-1">
                      {image.is_shared && (
                        <Badge className="bg-green-600/80 text-white text-xs">
                          Shared
                        </Badge>
                      )}
                    </div>

                    {/* Hover overlay with actions */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            size="sm"
                            onClick={() => setSelectedImage(image)}
                            className="bg-white/20 hover:bg-white/30 text-white text-xs px-3 py-1.5"
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl max-h-[90vh] w-[90vw] bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 border border-purple-500/30 overflow-y-auto backdrop-blur-xl">
                          {/* Background effects matching hero section */}
                          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-purple-600/10 pointer-events-none"></div>
                          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:50px_50px] pointer-events-none"></div>
                          
                          <DialogHeader className="relative z-10">
                            <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent flex items-center gap-2">
                              <ImageIcon className="w-6 h-6 text-blue-400" />
                              Image Details (Admin View)
                            </DialogTitle>
                          </DialogHeader>
                          
                          <div className="space-y-6 relative z-10">
                            {/* Image Display with enhanced styling */}
                            <div className="space-y-4">
                              <div className="flex justify-center">
                                <div className="relative group">
                                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                                  <img
                                    src={image.image_url}
                                    alt={image.prompt}
                                    className="relative max-w-full max-h-[400px] object-contain rounded-xl bg-black/20 backdrop-blur-sm shadow-2xl border border-white/10"
                                    style={{ maxWidth: '100%', height: 'auto' }}
                                  />
                                </div>
                              </div>
                            </div>
                            
                            {/* Details section with enhanced styling */}
                            <div className="space-y-4 bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10">
                              {/* Prompt section */}
                              <div>
                                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                                  <div className="w-5 h-5 text-purple-400" />
                                  Prompt
                                </h3>
                                <div className="bg-gradient-to-r from-slate-800/50 to-purple-900/20 p-4 rounded-xl border border-purple-500/20">
                                  <p className="text-gray-100 text-sm leading-relaxed max-h-32 overflow-y-auto">
                                    {image.prompt}
                                  </p>
                                </div>
                              </div>
                              
                              {/* User and Model Info */}
                              <div className="grid grid-cols-2 gap-4">
                                <div className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 p-4 rounded-xl border border-blue-500/30">
                                  <h4 className="text-sm font-medium text-blue-200 mb-2">User</h4>
                                  <p className="text-blue-100 font-medium">{image.profile_name}</p>
                                  <p className="text-blue-300 text-xs">{image.profile_email}</p>
                                </div>
                                <div className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 p-4 rounded-xl border border-purple-500/30">
                                  <h4 className="text-sm font-medium text-purple-200 mb-2">Model</h4>
                                  <p className="text-purple-100 font-medium">{image.model}</p>
                                </div>
                              </div>
                              
                              {/* Settings grid with enhanced styling */}
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                <div className="bg-gradient-to-br from-green-600/20 to-green-800/20 p-3 rounded-xl border border-green-500/30">
                                  <h4 className="text-xs font-medium text-green-200 mb-2">Size</h4>
                                  <p className="text-green-100 text-sm">{image.size}</p>
                                </div>
                                <div className="bg-gradient-to-br from-pink-600/20 to-pink-800/20 p-3 rounded-xl border border-pink-500/30">
                                  <h4 className="text-xs font-medium text-pink-200 mb-2">Quality</h4>
                                  <p className="text-pink-100 text-sm">{image.quality}</p>
                                </div>
                                <div className="bg-gradient-to-br from-yellow-600/20 to-yellow-800/20 p-3 rounded-xl border border-yellow-500/30">
                                  <h4 className="text-xs font-medium text-yellow-200 mb-2">Style</h4>
                                  <p className="text-yellow-100 text-sm">{image.style}</p>
                                </div>
                              </div>
                              
                              {/* Creation date */}
                              <div className="bg-gradient-to-r from-slate-700/30 to-slate-800/30 p-3 rounded-xl border border-slate-500/20">
                                <h4 className="text-xs font-medium text-slate-200 mb-1">Generated</h4>
                                <p className="text-slate-300 text-sm">{formatDate(image.created_at)}</p>
                              </div>
                              
                              {/* Sharing status */}
                              {image.is_shared && (
                                <div className="bg-gradient-to-r from-green-700/20 to-green-800/20 p-3 rounded-xl border border-green-500/30">
                                  <h4 className="text-xs font-medium text-green-200 mb-1">Sharing Status</h4>
                                  <p className="text-green-100 text-sm">Shared by {image.shared_by_admin_email}</p>
                                  <p className="text-green-300 text-xs">Shared on {formatDate(image.shared_at)}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>

                      <Button
                        size="sm"
                        onClick={() => generateShareLink(image.id)}
                        className="bg-white/20 hover:bg-white/30 text-white text-xs px-3 py-1.5"
                      >
                        <Share2 className="w-3 h-3" />
                      </Button>
                      
                      <Button
                        size="sm"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
                            try {
                              setDeleting(true);
                              const { error } = await supabase
                                .from('generated_images')
                                .delete()
                                .eq('id', image.id);
                              
                              if (error) throw error;
                              
                              toast.success('Image deleted successfully');
                              fetchImages();
                            } catch (error) {
                              console.error('Error deleting image:', error);
                              toast.error('Failed to delete image');
                            } finally {
                              setDeleting(false);
                            }
                          }
                        }}
                        className="bg-red-600/80 hover:bg-red-700 text-white text-xs px-3 py-1.5"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Image Information */}
                  <div className="p-3">
                    <p className="text-white text-xs mb-2 line-clamp-2 leading-relaxed">
                      {image.prompt.length > 100 ? `${image.prompt.slice(0, 100)}...` : image.prompt}
                    </p>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-blue-400 font-medium truncate mr-2">
                        {image.model
                          .replace(/\s*\(.*?\)/g, '')
                          .replace(/\s*-\s*(P1|P2|P3|P4|A4F|Via\s+provider\s+\d+)/gi, '')
                          .replace(/\s*-\s*Provider\s+\d+/gi, '')
                          .replace(/\s*via\s+provider\s+\d+/gi, '')
                          .replace(/provider-\d+\/*/gi, '')
                          .replace(/\s+/g, ' ')
                          .trim()}
                      </span>
                      <span className="text-gray-400">{image.size}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="text-gray-400 truncate mr-2">{formatDate(image.created_at)}</span>
                      <span className="text-gray-400 capitalize">{image.quality}</span>
                    </div>
                    
                    {/* User Information */}
                    <div className="text-xs text-gray-300 border-t border-white/10 pt-2">
                      <div className="font-medium">{image.profile_name}</div>
                      <div className="text-gray-400">{image.profile_email}</div>
                    </div>

                    {/* Admin Notes */}
                    {image.admin_notes && (
                      <div className="text-xs text-yellow-400 p-2 bg-yellow-500/10 rounded border border-yellow-500/20 mt-2">
                        <span className="font-medium">Notes:</span> {image.admin_notes}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredImages.length === 0 && !loading && (
            <div className="text-center text-gray-400 py-12">
              <ImageIcon className="w-12 h-12 mx-auto mb-4" />
              <p className="text-white mb-2">
                {totalCount === 0 ? 'No images found in database' : 'No images match your search criteria'}
              </p>
              <p className="text-sm text-gray-400">
                {searchTerm || filterShared !== 'all' 
                  ? 'Try adjusting your search or filters' 
                  : 'Images will appear here once users start generating them'}
              </p>
              {totalCount === 0 && (
                <Button 
                  onClick={fetchImages} 
                  className="mt-4 bg-blue-600 hover:bg-blue-700"
                >
                  Refresh
                </Button>
              )}
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-6">
              <Button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="bg-white/10 hover:bg-white/20 text-white border-white/20"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              
              <span className="text-white text-sm">
                Page {currentPage} of {totalPages}
              </span>
              
              <Button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="bg-white/10 hover:bg-white/20 text-white border-white/20"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Share Dialog */}
      {selectedImage && (
        <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
          <DialogContent className="bg-slate-900 border-white/20 text-white">
            <DialogHeader>
              <DialogTitle>Share Image</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-300 mb-2 block">
                  Recipient Email
                </label>
                <Input
                  type="email"
                  placeholder="user@example.com"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  className="bg-white/10 border-white/20 text-white"
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-300 mb-2 block">
                  Optional Message
                </label>
                <Textarea
                  placeholder="Add a personal message..."
                  value={shareMessage}
                  onChange={(e) => setShareMessage(e.target.value)}
                  className="bg-white/10 border-white/20 text-white"
                />
              </div>
              
              <div className="text-xs text-gray-400 p-3 bg-blue-500/10 rounded border border-blue-500/20">
                <p className="font-medium text-blue-300 mb-1">Note:</p>
                <p>• If the user has an account, they'll receive an in-app notification</p>
                <p>• A shareable link will be generated for manual sharing</p>
              </div>
              
              <Button
                onClick={shareImage}
                disabled={sharing}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600"
              >
                {sharing ? 'Sharing...' : 'Share Image'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AdminImageSharing;
